import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { contributionRequest, TIP_TAGS } from "../lib/contribution-client";
import { isAppsInTossChannel } from "../lib/channel";
import { PhotoAttach } from "../components/PhotoAttach";
import { TurnstileChallenge } from "../components/TurnstileChallenge";
import { type PublicTip } from "../components/TipList";
import {
	OUTBOX_SYNC_EVENT,
	listOutbox,
	removeOutbox,
	type OutboxOperation,
} from "../lib/outbox";
export function MyPostsPage() {
	const [tips, setTips] = useState<PublicTip[] | null>(null),
		[error, setError] = useState(""),
		[retry, setRetry] = useState(0),
		[pending, setPending] = useState<OutboxOperation[]>([]);
	useEffect(() => {
		let live = true;
		contributionRequest<{ tips: PublicTip[] }>("/api/tips/mine")
			.then((d) => {
				if (live) {
					setTips(d.tips);
					setError("");
				}
			})
			.catch((e) => {
				if (live) setError(e.message);
			});
		listOutbox()
			.then((rows) => {
				if (live)
					setPending(
						rows.filter((r) =>
							[
								"/api/tips",
								"/api/seat-requests",
								"/api/venue-requests",
								"/api/stage-reports",
								"/api/layout-corrections",
							].includes(r.endpoint),
						),
					);
			})
			.catch(() => {
				if (live) setError("전송함을 읽지 못했어요. 원문은 지우지 않았어요.");
			});
		return () => {
			live = false;
		};
	}, [retry]);
	useEffect(() => {
		const changed = () => setRetry((v) => v + 1);
		window.addEventListener(OUTBOX_SYNC_EVENT, changed);
		return () => window.removeEventListener(OUTBOX_SYNC_EVENT, changed);
	}, []);
	if (isAppsInTossChannel())
		return (
			<section className="page">
				<h1>내가 공개한 글</h1>
				<p>이 채널에서는 새 공개 글을 아직 열지 않았어요.</p>
			</section>
		);
	return (
		<section className="page narrow stack-lg">
			<h1>내가 공개한 글</h1>
			<p>
				이 기기 열쇠로 남긴 글만 보여요. 다른 기기에서 썼다면 설정에서 열쇠가
				포함된 백업을 복원해 주세요. 열쇠가 없으면 신고에서 내 글 삭제 요청을
				골라 주세요.
			</p>
			{error ? (
				<p role="alert">
					{error}
					<button type="button" onClick={() => setRetry(retry + 1)}>
						다시 불러오기
					</button>
				</p>
			) : tips === null ? (
				<p role="status">내 글을 불러오고 있어요.</p>
			) : tips.length ? (
				tips.map((tip) => (
					<EditableTip
						key={tip.id}
						tip={tip}
						refresh={() => setRetry(retry + 1)}
					/>
				))
			) : (
				<div className="stack">
					<p>아직 공개한 글이 없어요.</p>
					<Link to="/passport">최근 본 자리와 기억 보기</Link>
				</div>
			)}
			{pending.map((operation) =>
				operation.endpoint !== "/api/tips" ? (
					<QueuedContribution
						key={operation.clientOpId}
						operation={operation}
						refresh={() => setRetry(retry + 1)}
					/>
				) : (
					<QueuedTip
						key={operation.clientOpId}
						operation={operation}
						refresh={() => setRetry(retry + 1)}
					/>
				),
			)}
		</section>
	);
}
function EditableTip({
	tip,
	refresh,
}: {
	tip: PublicTip;
	refresh: () => void;
}) {
	const [note, setNote] = useState(tip.note),
		[tags, setTags] = useState(tip.tags),
		[month, setMonth] = useState(tip.observedMonth),
		[message, setMessage] = useState("");
	async function action(method: "PATCH" | "DELETE") {
		if (
			method === "DELETE" &&
			!confirm("이 한 줄과 사진을 공개 화면에서 지울까요?")
		)
			return;
		try {
			await contributionRequest(
				`/api/tips/${tip.id}`,
				method,
				method === "PATCH" ? { note, tags, observedMonth: month } : undefined,
			);
			setMessage(
				method === "PATCH"
					? "고친 내용을 저장했어요."
					: "공개 화면에서 지웠어요.",
			);
			refresh();
		} catch (e) {
			setMessage(
				e instanceof Error
					? e.message
					: "입력은 그대로 있어요. 다시 시도해 주세요.",
			);
		}
	}
	return (
		<article className="tip-item stack">
			<Link to={`/seat/${encodeURIComponent(tip.seatKey)}`}>
				{tip.sectionCode} {tip.rowLabel}열 {tip.seatNumber}번
			</Link>
			<label className="field">
				한 줄
				<textarea
					className="textarea"
					value={note}
					maxLength={300}
					onChange={(e) => setNote(e.target.value)}
				/>
			</label>
			<label className="field">
				관람 연월
				<input
					className="input"
					type="month"
					value={month}
					onChange={(e) => setMonth(e.target.value)}
				/>
			</label>
			<fieldset>
				<legend>태그</legend>
				{TIP_TAGS.map((tag) => (
					<label className="check-item" key={tag}>
						<input
							type="checkbox"
							checked={tags.includes(tag)}
							disabled={tags.length >= 4 && !tags.includes(tag)}
							onChange={(e) =>
								setTags(
									e.target.checked
										? [...tags, tag]
										: tags.filter((t) => t !== tag),
								)
							}
						/>
						{tag}
					</label>
				))}
			</fieldset>
			<div className="inline">
				<button
					type="button"
					className="button"
					disabled={note.trim().length < 10}
					onClick={() => void action("PATCH")}
				>
					고친 내용 저장하기
				</button>
				<button
					type="button"
					className="button button-danger"
					onClick={() => void action("DELETE")}
				>
					한 줄과 사진 지우기
				</button>
			</div>
			<p role="status">{message}</p>
			<p>
				{tip.photoStatus === "pending"
					? "사진 확인 대기"
					: tip.photoStatus === "rejected"
						? `사진을 공개하지 않았어요. ${tip.photoNote ?? ""}`
						: tip.photoStatus === "approved"
							? "사진을 공개했어요."
							: ""}
			</p>
			<PhotoAttach reviewId={tip.id} />
		</article>
	);
}
function QueuedTip({
	operation,
	refresh,
}: {
	operation: OutboxOperation;
	refresh: () => void;
}) {
	const [token, setToken] = useState<string | null>(null),
		[message, setMessage] = useState("");
	const accept = useCallback((value: string | null) => setToken(value), []);
	async function send() {
		try {
			await contributionRequest(
				"/api/tips",
				"POST",
				operation.body,
				token ?? undefined,
			);
			await removeOutbox(operation.clientOpId);
			refresh();
		} catch (e) {
			setMessage(e instanceof Error ? e.message : "아직 보내지 못했어요.");
		}
	}
	return (
		<section className="stack">
			<h2>전송 대기 한 줄</h2>
			<p>{String((operation.body as { note?: string }).note ?? "")}</p>
			<TurnstileChallenge onToken={accept} />
			<button className="button" disabled={!token} onClick={() => void send()}>
				확인하고 공개하기
			</button>
			<button
				className="button"
				onClick={() => void removeOutbox(operation.clientOpId).then(refresh)}
			>
				대기 초안 지우기
			</button>
			<p role="status">{message}</p>
		</section>
	);
}

function QueuedContribution({
	operation,
	refresh,
}: {
	operation: OutboxOperation;
	refresh: () => void;
}) {
	const [message, setMessage] = useState("");
	const data = operation.body as {
		name?: string;
		showTitle?: string;
		body?: string;
	};
	async function send() {
		try {
			await contributionRequest(
				operation.endpoint,
				"POST",
				operation.body,
				undefined,
				operation.clientOpId,
			);
			await removeOutbox(operation.clientOpId);
			refresh();
		} catch (error) {
			setMessage(
				error instanceof Error ? error.message : "연결되면 다시 시도해 주세요.",
			);
		}
	}
	return (
		<section className="stack">
			<h2>전송 대기 요청·제보</h2>
			<p>
				{data.name ??
					data.showTitle ??
					data.body ??
					"궁금한 자리 요청을 기기에 보관했어요."}
			</p>
			<p>아직 공개·접수된 요청이 아니에요.</p>
			<button className="button" onClick={() => void send()}>
				지금 다시 전송하기
			</button>
			<button
				className="button"
				onClick={() => void removeOutbox(operation.clientOpId).then(refresh)}
			>
				대기 요청 지우기
			</button>
			<p role="status">{message}</p>
		</section>
	);
}
